/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import { Close16 } from '@carbon/icons-react';
import React, { useMemo } from 'react';
import {
  MultiValueProps,
  OptionProps,
  SingleValueProps,
  components,
} from 'react-select';
import styled, { css } from 'styled-components';
import { User } from 'api/models';
import { FieldOption } from 'api/tasks';
import { Avatar } from 'components/atoms/avatar';
import { Select } from 'components/atoms/select';
import { Color } from 'components/colors';
import { getRandomColor } from 'utils/get-random-color';
import { CpColorTag } from '../../tags/cp-tags';

export const TagForUser = styled(CpColorTag)`
  display: flex;
  align-items: center;
  padding: var(--cp-spacing-02) !important;
  padding-right: var(--cp-spacing-03) !important;
  margin: 0 !important;
`;

export const CpSelect = styled(Select)<{ noBorder?: boolean }>`
  .ozone-select__control:hover {
    background: var(--cp-gray-10);
  }

  .ozone-select__control {
    ${(props) =>
      props?.noBorder
        ? css`
            border-bottom: none !important;
          `
        : css`
            border-bottom: 1px solid var(--cp-border-strong) !important;
          `};
    background: var(--cp-white);

    .ozone-select__indicators svg {
      fill: var(--cp-text-primary);
    }
  }
  .ozone-select__control.ozone-select__control--menu-is-open {
    outline: 2px solid var(--cp-blue-60) !important;
    background: var(--cp-gray-10);
  }

  .ozone-select__multi-value__label {
    padding: 0;
  }

  ${CpColorTag} {
    width: fit-content;
  }

  .ozone-select__single-value
    ${CpColorTag},
    .ozone-select__option
    ${CpColorTag} {
    padding: var(--cp-spacing-02) var(--cp-spacing-04) !important;
  }
  .ozone-select__single-value
    ${TagForUser},
    .ozone-select__option
    ${TagForUser} {
    padding: var(--cp-spacing-02) !important;
    padding-right: var(--cp-spacing-03) !important;
  }

  .ozone-select__menu {
    border-radius: var(--border-radius-small);
    box-shadow: var(--shadow-08);
    width: calc(100% - 5px);
    overflow-x: hidden;
    z-index: 100;
  }

  .ozone-select__placeholder {
    margin-left: var(--cp-spacing-03);
    font-size: var(--cp-body-01-font-size);
    color: var(--cp-text-placeholder);
  }
`;

export type CpUserOption = {
  label: string;
  value: number;
  color: Color;
  user: User;
};

const StyledMultiValue = styled(components.MultiValue)<{
  color: Color;
}>`
  background: ${(props) => `var(--cp-${props.color}-10)`};
  border-radius: 100px;
  overflow: hidden;
  padding-right: var(--cp-spacing-01);

  ${CpColorTag} {
    padding: var(--cp-spacing-02) var(--cp-spacing-04) !important;
    &:has(svg) {
      padding-right: var(--cp-spacing-02) !important;
    }
  }

  ${TagForUser} {
    padding: var(--cp-spacing-02) !important;
    padding-right: 0 !important;
  }

  .ozone-select__multi-value__remove {
    height: 26px;
    width: 26px;
    margin-top: 1px;

    :active {
      border: 1px solid ${(props) => `var(--cp-${props.color}-60)`};
    }
    :hover {
      background: ${(props) => `var(--cp-${props.color}-20)`};
    }
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
      transform: translateX(-1px);
    }
  }

  &:has(${TagForUser}) {
    .ozone-select__multi-value__remove {
      margin-left: var(--cp-spacing-01);
    }
    svg {
      transform: translateX(-1px);
    }
  }
`;

export const CpTagOption = (props) => {
  const data = props?.data as FieldOption<number>;

  return (
    <components.Option {...props}>
      <CpColorTag color={data.color ?? getRandomColor(data.label)}>
        {data.label}
      </CpColorTag>
    </components.Option>
  );
};

export const CpTagSingleValue = (
  props: SingleValueProps<FieldOption<number>>,
) => {
  const data = props?.data;

  return (
    <components.SingleValue {...props}>
      <CpColorTag color={data.color ?? getRandomColor(data.label)}>
        {data.label}
      </CpColorTag>
    </components.SingleValue>
  );
};

export const CpTagMultiValue = (
  props: MultiValueProps<FieldOption<number>>,
) => {
  const data = props?.data as FieldOption<number>;

  const color = useMemo(
    () => data.color ?? getRandomColor(data.label),
    [data.color, data.label],
  );

  return (
    <StyledMultiValue {...props} color={color}>
      <CpColorTag color={color}>{data.label}</CpColorTag>
    </StyledMultiValue>
  );
};

export const MultiValueRemove = (
  props: MultiValueProps<FieldOption<number | 'more'>>,
) => {
  const value = props?.data?.value as number | 'more';

  if (value === 'more') {
    return null;
  }

  return (
    <components.MultiValueRemove {...props}>
      <Close16 />
    </components.MultiValueRemove>
  );
};

export const CpUserSingleValue = (props: SingleValueProps<CpUserOption>) => {
  const data = props?.data;

  return (
    <components.SingleValue {...props}>
      <TagForUser color={data.color ?? getRandomColor(data.label)}>
        <Avatar size="small" name={data?.label} src={data?.user?.photoUrl} />
        {data.label}
      </TagForUser>
    </components.SingleValue>
  );
};

export const CpUserMultiValue = (props: MultiValueProps<CpUserOption>) => {
  const data = props?.data;

  return (
    <StyledMultiValue
      color={data.color ?? getRandomColor(data.label)}
      {...props}
    >
      <TagForUser color={data.color ?? getRandomColor(data.label)}>
        <Avatar size="small" name={data?.label} src={data?.user?.photoUrl} />
        {data.label}
      </TagForUser>
    </StyledMultiValue>
  );
};

export const CpUserOption = (props: OptionProps<CpUserOption, false>) => {
  const data = props?.data as CpUserOption;

  return (
    <components.Option {...props}>
      <TagForUser color={data.color ?? getRandomColor(data.label)}>
        <Avatar size="small" name={data?.label} src={data?.user?.photoUrl} />
        {data.label}
      </TagForUser>
    </components.Option>
  );
};

export const CpTagComponents = {
  Option: CpTagOption,
  SingleValue: CpTagSingleValue,
  MultiValue: CpTagMultiValue,
  MultiValueRemove,
};

export const CpUserComponents = {
  MultiValueRemove,
  SingleValue: CpUserSingleValue,
  MultiValue: CpUserMultiValue,
  Option: CpUserOption,
};
